/**
 * @file Pile.tsx
 * @description
 * Represents a single pile component on the game board.
 * Displays the top card value and its type (ascending/descending).
 * Acts as a potential click target for cards.
 *
 * @dependencies
 * - React
 *
 * @notes
 * - Basic styling applied directly; will integrate theme/CSS and color-blind cues later.
 * - Now supports click-to-play interaction via `onClick` prop.
 * - `isDropTarget` is used for highlighting the pile when a card is selected.
 */

import React from 'react';

interface PileProps {
  /** Unique identifier for the pile (e.g., 'asc1', 'desc2'). */
  id: string;
  /** The orientation type of the pile ('asc' or 'desc'). */
  type: 'asc' | 'desc';
  /** The current top card value on the pile. */
  topValue: number;
  /** Function to call when this pile is clicked (for click-to-play). */
  onClick?: (pileId: string) => void;
  /** Optional boolean to indicate if this pile is a valid target for the currently selected card. */
  isDropTarget?: boolean;
}

const Pile: React.FC<PileProps> = ({ id, type, topValue, onClick, isDropTarget = false }) => {

  // Basic styling - will be replaced by proper CSS classes and theming later
  const pileStyle: React.CSSProperties = {
    width: '80px',
    height: '110px',
    border: `2px ${isDropTarget ? 'solid' : 'dashed'} ${isDropTarget ? (type === 'asc' ? '#3b82f6' : '#ef4444') : '#ccc'}`, // Highlight valid targets with solid border
    borderRadius: '5px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    // margin: '10px', // Margin is handled by the parent grid/flex container
    backgroundColor: isDropTarget ? (type === 'asc' ? '#dbeafe' : '#fee2e2') : (type === 'asc' ? '#eff6ff' : '#fef2f2'), // Lighter background when highlighted
    transition: 'border-color 0.2s ease-in-out, background-color 0.2s ease-in-out',
    position: 'relative', // Needed for absolute positioning of labels
     cursor: onClick ? 'pointer' : 'default', // Always indicate clickable if onClick is provided
      // Optional: Add a subtle hover effect for clickable piles
      ...(onClick && {
         ':hover': {
            borderColor: isDropTarget ? (type === 'asc' ? '#1d4ed8' : '#b91c1c') : '#999', // Darken border on hover
            backgroundColor: isDropTarget ? (type === 'asc' ? '#bfdbfe' : '#fecaca') : (type === 'asc' ? '#dbeafe' : '#fee2e2'), // Darken background on hover
         } as React.CSSProperties, // Type assertion needed for nested pseudo-class
     })
  };

  const labelStyle: React.CSSProperties = {
    fontSize: '0.8em',
    color: '#555',
    position: 'absolute',
    top: '5px',
    left: '5px',
  };

  const valueStyle: React.CSSProperties = {
    fontSize: '2em',
    fontWeight: 'bold',
    color: type === 'asc' ? '#1d4ed8' : '#b91c1c', // Darker blue/red for value
  };

  const handlePileClick = () => {
      if (onClick) {
          onClick(id); // Call the passed-in click handler with the pile's ID
      }
  };


  return (
    <div
      style={pileStyle}
      onClick={handlePileClick} // Use the new click handler
      aria-label={`${type === 'asc' ? 'Ascending' : 'Descending'} pile, top card ${topValue}`}
      role="button" // Indicate that the element is interactive
    >
      <div style={labelStyle}>{type.toUpperCase()}</div>
      <div style={valueStyle}>{topValue}</div>
      {/* Could add a small texture/icon here for color-blind support */}
    </div>
  );
};

export default Pile;